import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './restaurant.css';
import Carousel from 'react-elastic-carousel';
import logo3 from '../../../assets/images/No-Image.jpg';
import ApiService from '../../../services/apiService';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';

const breakPoints = [
  { width: 1, itemsToShow: 1 },
  { width: 550, itemsToShow: 1, itemsToScroll: 2, pagination: false },
  { width: 850, itemsToShow: 2 },
  { width: 1150, itemsToShow: 3, itemsToScroll: 2 },
  { width: 1450, itemsToShow: 4 },
  { width: 1750, itemsToShow: 5 },
];

const RestaurantSlider = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const [show, setShow] = useState(false);
  const ImageUrl = process.env.REACT_APP_IMAGE_URL || '';

  useEffect(() => {
    GetRestaurant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const GetRestaurant = async () => {
    setLoading(true);
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_RESTAURANT');
      const list = res?.data?.result || [];
      setRestaurants(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      toast.error(error?.response?.data?.message || 'Failed to fetch restaurants');
    } finally {
      setLoading(false);
    }
  };

  const handleViewToggle = (item) => {
    setSelected(item);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setSelected(null);
  };

  const UpdateItem = async () => {
    if (!selected) return;
    try {
      const apiService = new ApiService();
      const nextActive = !selected.isActive;

      const body = {
        id: selected?.id,
        name: selected?.name,
        imageUrl: selected?.imageUrl,
        noOfEmployees: selected?.noOfEmployees,
        address: selected?.address,
        loggedInUserId: selected?.loggedInUserId,
        isActive: nextActive,
      };

      const res = await apiService.put('UPDATE_RESTAURANT', body);

      if (res?.data?.statusCode === 200) {
        // update local state instead of reloading
        setRestaurants((prev) => prev.map((r) => (r.id === selected.id ? { ...r, isActive: nextActive } : r)));
        toast.success('Restaurant successfully updated');
        handleClose();
      } else {
        toast.warning(res?.data?.message || 'Waiting for restaurant update');
      }
    } catch (error) {
      console.error('Update restaurant failed:', error);
      toast.error(error?.response?.data?.message || 'Failed to update restaurant');
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return restaurants;
    return restaurants.filter((r) => {
      const name = (r?.name || '').toLowerCase();
      const addr = (r?.address || '').toLowerCase();
      return name.includes(q) || addr.includes(q);
    });
  }, [restaurants, searchTerm]);

  const handleAddress = (val) => {
    const addr = val?.address || '';
    return addr.length > 25 ? addr.substring(0, 25) + '…' : addr;
  };

  const imgSrc = (val) => {
    const path = val?.imageUrl ? String(val.imageUrl) : '';
    if (!path) return logo3;
    // safe join; avoid double slashes
    const base = ImageUrl.endsWith('/') ? ImageUrl.slice(0, -1) : ImageUrl;
    const rel = path.startsWith('/') ? path.slice(1) : path;
    return `${base}/${rel}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
        <span className="text-muted">Loading restaurants…</span>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        {/* <h5 className="mb-0">Restaurants</h5> */}
        <input
          className="form-control"
          style={{ maxWidth: 320 }}
          placeholder="Search by name or address"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center text-muted py-5">No restaurants found.</div>
      ) : (
        <Carousel loop breakPoints={breakPoints} className="d-flex justify-content-center">
          {filtered.map((val) => (
            <div key={val?.id ?? val?.name}>
              <div className="res-card">
                <div className="row">
                  <img
                    src={imgSrc(val)}
                    alt={val?.name || 'Restaurant'}
                    className="res-cardImg"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = logo3;
                    }}
                  />
                </div>
                <div className="d-flex justify-content-between p-3">
                  <div>
                    <div className="res-title">Name :</div>
                    <div className="res-title">Address :</div>
                    <div className="res-title">Employees :</div>
                  </div>
                  <div className="">
                    <div className="res-txt">{val?.name || '-'}</div>
                    <div className="res-txt">{handleAddress(val) || '-'}</div>
                    <div className="res-txt">{val?.noOfEmployees ?? 0}</div>
                  </div>
                </div>
              </div>
              <div className="center gap-3 p-3">
                <button className="btn btn-primary col-6" onClick={() => navigate('/admin/company/create', { state: { restaurant: val, isEditMode: true } })}>
                  Edit
                </button>
                <button className={`btn ${val?.isActive ? 'btn-danger' : 'btn-success'} col-6`} onClick={() => handleViewToggle(val)}>
                  {val?.isActive ? 'Inactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </Carousel>
      )}

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{selected?.isActive ? 'Inactivate' : 'Activate'} Restaurant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to {selected?.isActive ? 'inactivate' : 'activate'} <strong>{selected?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant={selected?.isActive ? 'danger' : 'success'} onClick={UpdateItem}>
            {selected?.isActive ? 'Inactivate' : 'Activate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RestaurantSlider;
